import { defineStore } from "pinia"
import { computed, ref } from "vue"
import { toast } from "vue-sonner"
import { CensusUploadJob } from "@vitable/asclepius-api-client-ts"
import { queryClient } from "@/config/query-client"
import useAsclepius from "@common/composables/api/asclepius"
import { QueryApiCacheKeys } from "@common/config/query-api-cache-keys"
import eventBus from "@common/events/common-event-bus"
import events from "@/modules/member-management/events/events"

const POLL_INTERVAL_MS = 2000

export const useCensusUploadStore = defineStore("censusUploadStore", () => {
  const { getCensusApi } = useAsclepius()
  const MAX_CONSECUTIVE_ERRORS = 3

  // State
  const activeJobId = ref<string | null>(null)
  const activeCompanyId = ref<string | null>(null)
  const activeJob = ref<CensusUploadJob | null>(null)
  let pollTimer: ReturnType<typeof setInterval> | null = null
  let consecutiveErrors = 0
  let isPolling = false
  let trackingToken = 0

  // Getters
  const isUploadInProgress = computed(() => {
    if (activeJobId.value === null) return false
    if (!activeJob.value) return true
    return activeJob.value.status === "pending" || activeJob.value.status === "in_progress"
  })

  function isUploadInProgressForCompany(companyId: string | null | undefined) {
    return !!companyId && activeCompanyId.value === companyId && isUploadInProgress.value
  }

  // Actions
  function startTracking(companyId: string, uploadJobId: string) {
    stopTracking()
    trackingToken++
    const currentTrackingToken = trackingToken
    activeCompanyId.value = companyId
    activeJobId.value = uploadJobId
    activeJob.value = null
    consecutiveErrors = 0
    pollTimer = setInterval(() => poll(currentTrackingToken), POLL_INTERVAL_MS)
  }

  function stopTracking(expectedTrackingToken?: number) {
    if (expectedTrackingToken !== undefined && expectedTrackingToken !== trackingToken) return

    trackingToken++
    if (pollTimer) {
      clearInterval(pollTimer)
      pollTimer = null
    }
    activeJobId.value = null
    activeCompanyId.value = null
    activeJob.value = null
  }

  function $reset() {
    stopTracking()
    consecutiveErrors = 0
    isPolling = false
  }

  function isCurrentTrackingRequest(expectedTrackingToken: number, companyId: string, uploadJobId: string) {
    return (
      expectedTrackingToken === trackingToken &&
      activeCompanyId.value === companyId &&
      activeJobId.value === uploadJobId
    )
  }

  async function poll(expectedTrackingToken = trackingToken) {
    if (!activeJobId.value || !activeCompanyId.value || isPolling) return

    const expectedCompanyId = activeCompanyId.value
    const expectedJobId = activeJobId.value
    isPolling = true

    try {
      const response = await getCensusApi().getCensusUploadJob({
        companyId: expectedCompanyId,
        uploadJobId: expectedJobId
      })

      if (!isCurrentTrackingRequest(expectedTrackingToken, expectedCompanyId, expectedJobId)) return

      activeJob.value = response.data
      consecutiveErrors = 0

      if (response.data.status === "completed") {
        const summary = response.data.result_summary
        const added = summary?.added ?? 0
        const reactivated = summary?.reactivated ?? 0
        const removed = summary?.removed ?? 0
        const total = added + reactivated + removed
        await queryClient.invalidateQueries({
          queryKey: [QueryApiCacheKeys.COMPANY_MEMBERS, expectedCompanyId]
        })
        if (!isCurrentTrackingRequest(expectedTrackingToken, expectedCompanyId, expectedJobId)) return

        toast.success(`Census uploaded successfully — ${total} employee${total !== 1 ? "s" : ""} processed.`)
        eventBus.$emit(events.CENSUS_UPLOADED)
        eventBus.$emit(events.COMPANY_MEMBERS_CHANGED)
        stopTracking(expectedTrackingToken)
      } else if (response.data.status === "failed") {
        toast.error("Census upload failed. Please try again.")
        stopTracking(expectedTrackingToken)
      }
    } catch {
      if (!isCurrentTrackingRequest(expectedTrackingToken, expectedCompanyId, expectedJobId)) return

      consecutiveErrors++
      if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
        toast.error("Unable to check census upload status. Please refresh the page.")
        stopTracking(expectedTrackingToken)
      }
    } finally {
      isPolling = false
    }
  }

  return {
    activeJobId,
    activeCompanyId,
    activeJob,
    isUploadInProgress,
    isUploadInProgressForCompany,
    startTracking,
    stopTracking,
    $reset
  }
})
